import useCartStore from '~stores/cart';
/**
 * Go through each discount object and determine if type is excluded.
 *  @params {Object} entry - Discount object.
 *  @params {Object} logic - Product object.
 * - Exclusions override inclusions.
 * - If an earlier type is excluded then skip checking and return false.
 * - If an earlier type is included then continue checking exclusions to
 *   make sure a later type doesn't mean the discount is excluded.
 */
export function checkIncludedOrExcludedProducts(entry, logic) {
  let excluded = false;
  let included = false;

  if (logic?.collectionIds?.length) {
    excluded = entry.exclude.collectionIds?.some((id) => {
      return logic.collectionIds.includes(Number(id));
    });

    included = entry.include.collectionIds?.some((id) => {
      return logic.collectionIds.includes(Number(id));
    });
  }

  if (logic?.productId && !excluded) {
    excluded = entry.exclude.productIds?.some((id) => {
      return logic.productId === Number(id);
    });

    if (!included) {
      included = entry.include.productIds?.some((id) => {
        return logic.productId === Number(id);
      });
    }
  }

  if (logic?.variantId && !excluded) {
    excluded = entry.exclude.variantIds?.some((id) => {
      return logic.variantId === Number(id);
    });

    if (!included) {
      included = entry.include.variantIds?.some((id) => {
        return logic.variantId === Number(id);
      });
    }
  }

  if (excluded) {
    return false;
  }

  return included;
}

/**
 * `cnvs.discount.*` is inlined once by the server at page load
 * (theme-variables.liquid) and never reassigned afterwards, so the
 * valid-code lists below only need to be derived once per page rather
 * than re-mapped on every call (these run per product card).
 */
let validCampaignCodesCache;
let validOtherCampaignCodesCache;
let validInfluencerCodesCache;
let validPrivateCodesCache;

/**
 * Compute array of valid campaign codes.
 * @returns {Array}
 */
export function validCampaignCodes() {
  if (!validCampaignCodesCache) {
    validCampaignCodesCache = cnvs.discount.campaigns
      .filter((campaign) => campaign.type !== 'influencer')
      .map((campaign) => campaign.code.map((code) => code.toUpperCase()))
      .flat();
  }
  return validCampaignCodesCache;
}

export function validOtherCampaignCodes() {
  if (!validOtherCampaignCodesCache) {
    validOtherCampaignCodesCache = cnvs.discount.campaignCodes.map((code) =>
      code.toUpperCase(),
    );
  }
  return validOtherCampaignCodesCache;
}

/**
 * Compute array of valid influencer codes.
 * @returns {Array}
 */
export function validInfluencerCodes() {
  if (!validInfluencerCodesCache) {
    validInfluencerCodesCache = cnvs.discount.influencers.map((code) =>
      code.toUpperCase(),
    );
  }
  return validInfluencerCodesCache;
}

export function validPrivateCodes() {
  if (!validPrivateCodesCache) {
    validPrivateCodesCache = cnvs.discount.privateCampaigns
      .map((campaign) => campaign.code.toUpperCase())
      .flat();
  }
  return validPrivateCodesCache;
}

export function getTypes(code) {
  const upperCode = code.toUpperCase();

  if (validPrivateCodes().includes(upperCode)) {
    return 'priv';
  }

  if (
    validOtherCampaignCodes().includes(upperCode) ||
    validCampaignCodes().includes(upperCode)
  ) {
    return 'pub';
  }

  if (validInfluencerCodes().includes(upperCode)) {
    return 'infl';
  }

  // check if the code is an order type discount
  // order type discounts are fixed amount discounts that are applied to the entire order
  const cartStore = useCartStore();
  const orderType = cartStore.response.cart_level_discount_applications.find(
    (app) =>
      app.title.toUpperCase() === upperCode &&
      app.target_selection === 'all' &&
      app.value !== '0.0' &&
      app.total_allocated_amount !== 0,
  );

  if (orderType) {
    return 'order';
  }

  const productType = getProductCode(upperCode);
  if (productType) {
    return 'product';
  }

  // if it stays empty, it must be shipping type discount or invalid
  return 'shipping';
}

export function getProductCode(code) {
  const cartStore = useCartStore();
  const codeInCartLevel =
    cartStore.response.cart_level_discount_applications.find((app) => {
      return app.title.toUpperCase() === code;
    });

  const cartItemsDiscounts = cartStore.response.items.map((item) => {
    return item.discounts.map((discount) => {
      return discount.title.toUpperCase();
    });
  });

  const hasProductDiscount = cartItemsDiscounts
    ? cartItemsDiscounts.some((discount) => {
        return discount.includes(code);
      })
    : false;

  if (!codeInCartLevel && hasProductDiscount) {
    return 'product';
  }

  return '';
  // line_level_discount_allocations
}

/**
 * Get the campaign code currently applied to the cart.
 * - Prefers a code Shopify still considers `applicable`, since an old/replaced
 *   code can linger in `discount_codes` (not yet applicable) alongside the
 *   newly-applied one - taking the first match regardless of `applicable`
 *   would then pick the stale code and feed it into discount-rule lookups
 *   (see `getCampaignDiscount()`), applying the wrong campaign's rules.
 * - Falls back to the first pub/infl/priv-typed code regardless of
 *   `applicable` if none qualifies, to preserve prior behaviour when nothing
 *   is (yet) marked applicable.
 * @returns {String|undefined} - The campaign code, or undefined if none found.
 */
/**
 * Cache keyed by the `discount_codes` array reference - the cart store always
 * replaces `response` wholesale on updates (never mutates it in place), so a
 * stable reference reliably means "nothing changed" and skips redoing this
 * scan for every product card that calls getCampaignCode() on the same render.
 */
let campaignCodeCache = { discountCodes: null, result: null };

export function getCampaignCode() {
  const cartStore = useCartStore();
  const discountCodes = cartStore.response.discount_codes;

  if (campaignCodeCache.discountCodes === discountCodes) {
    return campaignCodeCache.result;
  }

  const campaignTypes = ['pub', 'infl', 'priv'];
  const isCampaignCode = (code) => campaignTypes.includes(getTypes(code));

  const applicableCode = discountCodes.find(
    (discount) =>
      discount.applicable === true &&
      isCampaignCode(discount.code.toUpperCase()),
  );

  const result = applicableCode
    ? applicableCode.code.toUpperCase()
    : discountCodes
        .map((discount) => discount.code.toUpperCase())
        .find(isCampaignCode);

  campaignCodeCache = { discountCodes, result };
  return result;
}

export function isOrderCode(code) {
  return getTypes(code) === 'order';
}

/**
 * Check if a campaign's code is the one actually applied to the cart.
 * - The Pinia `discount-campaigns` store's `active`/`promoted` state persists
 *   to localStorage and is repopulated asynchronously on hydration, so it can
 *   briefly (or, under a race, persistently) point at a stale campaign while
 *   `cart.discount_codes` / `cart.attributes.discount_codes` already reflect
 *   the real, newly-applied code. Callers that render price/discount info
 *   should confirm against the cart before trusting the campaign object.
 * @param {Object} campaign - Campaign object with a `code` array.
 * @param {Object} cart - Cart store response.
 * @returns {Boolean}
 */
export function isCampaignAppliedInCart(campaign, cart) {
  if (!campaign || !campaign.code?.length) {
    return false;
  }

  const codes = campaign.code.map((code) => code.toUpperCase());

  const isApplicableDiscountCode = (cart?.discount_codes || []).some(
    (discount) =>
      codes.includes(discount.code.toUpperCase()) &&
      discount.applicable === true,
  );

  if (isApplicableDiscountCode) {
    return true;
  }

  const cartCampaignCode = (cart?.attributes?.discount_codes || '').split(
    ':',
  )[0];

  return Boolean(
    cartCampaignCode && codes.includes(cartCampaignCode.toUpperCase()),
  );
}

/**
 * Check if a discount applies to the current code
 * @param {Object} discount - Discount entry with applyTo and specificInflCodes/specInflCodes
 * @param {String} currentCode - Currently active discount code
 * @returns {Boolean}
 */
export function isDiscountApplicableForCode(discount, currentCode) {
  if (!discount.applyTo || discount.applyTo === 'allCodes') {
    return true;
  }

  const codeUpper = currentCode?.toUpperCase() || '';

  if (discount.applyTo === 'specificInflCodes') {
    // Handle string format (comma-separated) for regular discounts
    const codes = (discount.specificInflCodes || '')
      .split(',')
      .map((discountCode) => discountCode.trim().toUpperCase())
      .filter(Boolean);
    return codes.includes(codeUpper);
  }

  if (discount.applyTo === 'inflCodes') {
    return validInfluencerCodes().includes(codeUpper);
  }

  // Handle array format for conditionalDiscounts (specInflCodes)
  if (Array.isArray(discount.specInflCodes) && discount.specInflCodes.length) {
    return discount.specInflCodes
      .map((discountCode) => discountCode.toUpperCase())
      .includes(codeUpper);
  }

  return true;
}
